import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Modal,
  Avatar,
  Progress,
  Badge,
  Tooltip,
  Dropdown,
  Menu,
  Timeline,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ExportOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  TruckOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  DollarOutlined,
  CalendarOutlined,
  MoreOutlined,
  SendOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import type { PurchaseOrder, Vendor, PurchaseOrderStatus, Priority } from '../../types';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface PurchaseOrdersProps {
  purchaseOrders: PurchaseOrder[];
  vendors: Vendor[];
  onOrderCreate: () => void;
  onOrderUpdate: (id: string, data: any) => void;
  onOrderDelete: (id: string) => void;
}

export const PurchaseOrders: React.FC<PurchaseOrdersProps> = ({
  purchaseOrders,
  vendors,
  onOrderCreate,
  onOrderUpdate,
  onOrderDelete,
}) => {
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<PurchaseOrderStatus | undefined>();
  const [vendorFilter, setVendorFilter] = useState<string | undefined>();
  const [priorityFilter, setPriorityFilter] = useState<Priority | undefined>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Mock data for demonstration
  const mockVendors: Vendor[] = [
    {
      id: '1',
      code: 'V001',
      name: 'ABC Supplies Inc.',
      type: 'supplier',
      category: 'office_supplies',
      status: 'active',
      contactInfo: {
        primaryContact: {
          name: 'John Smith',
          title: 'Sales Manager',
          email: 'john@abcsupplies.com',
          phone: '+1-555-0123',
        },
      },
      address: {
        billing: {
          street: '123 Business St',
          city: 'New York',
          state: 'NY',
          postalCode: '10001',
          country: 'USA',
        },
      },
      paymentTerms: {
        termCode: 'NET30',
        description: 'Net 30 days',
        daysNet: 30,
        currency: 'USD',
        paymentMethod: 'bank_transfer',
      },
      taxInfo: {
        taxId: '12-3456789',
        taxExempt: false,
        taxCategory: 'standard',
      },
      documents: [],
      rating: {
        overall: 4.5,
        quality: 4.0,
        delivery: 5.0,
        service: 4.5,
        price: 4.0,
        totalOrders: 25,
        onTimeDelivery: 95,
        lastEvaluated: new Date(),
      },
      certifications: [],
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: 'admin',
    },
  ];

  const mockPurchaseOrders: PurchaseOrder[] = [
    {
      id: '1',
      orderNumber: 'PO-2024-001',
      vendorId: '1',
      vendor: mockVendors[0],
      orderDate: new Date('2024-01-15'),
      expectedDeliveryDate: new Date('2024-02-01'),
      status: 'approved',
      priority: 'normal',
      paymentTerms: mockVendors[0].paymentTerms,
      shippingAddress: {
        street: '456 Company Ave',
        city: 'New York',
        state: 'NY',
        postalCode: '10002',
        country: 'USA',
      },
      billingAddress: {
        street: '456 Company Ave',
        city: 'New York',
        state: 'NY',
        postalCode: '10002',
        country: 'USA',
      },
      items: [
        {
          id: '1',
          lineNumber: 1,
          productName: 'A4 Paper',
          description: 'White A4 copy paper, 80gsm',
          quantity: 50,
          unitOfMeasure: 'ream',
          unitPrice: 8.50,
          totalPrice: 425,
          taxRate: 8.25,
          taxAmount: 35.06,
          discountPercent: 0,
          discountAmount: 0,
          netAmount: 460.06,
          expectedDeliveryDate: new Date('2024-02-01'),
          receivedQuantity: 0,
          invoicedQuantity: 0,
          status: 'confirmed',
        },
      ],
      subtotal: 425,
      taxAmount: 35.06,
      shippingCost: 0,
      discountAmount: 0,
      totalAmount: 460.06,
      currency: 'USD',
      approvalFlow: {} as any,
      approvals: [],
      receipts: [],
      invoices: [],
      attachments: [],
      terms: 'Standard terms and conditions apply',
      createdBy: 'john.doe',
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date('2024-01-15'),
    },
    {
      id: '2',
      orderNumber: 'PO-2024-002',
      vendorId: '1',
      vendor: mockVendors[0],
      orderDate: new Date('2024-01-20'),
      expectedDeliveryDate: new Date('2024-02-15'),
      status: 'sent_to_vendor',
      priority: 'high',
      paymentTerms: mockVendors[0].paymentTerms,
      shippingAddress: {
        street: '456 Company Ave',
        city: 'New York',
        state: 'NY',
        postalCode: '10002',
        country: 'USA',
      },
      billingAddress: {
        street: '456 Company Ave',
        city: 'New York',
        state: 'NY',
        postalCode: '10002',
        country: 'USA',
      },
      items: [],
      subtotal: 25000,
      taxAmount: 2062.50,
      shippingCost: 0,
      discountAmount: 0,
      totalAmount: 27062.50,
      currency: 'USD',
      approvalFlow: {} as any,
      approvals: [],
      receipts: [],
      invoices: [],
      attachments: [],
      terms: 'Standard terms and conditions apply',
      createdBy: 'jane.smith',
      createdAt: new Date('2024-01-20'),
      updatedAt: new Date('2024-01-20'),
    },
  ];

  const [filteredData, setFilteredData] = useState(mockPurchaseOrders);

  useEffect(() => {
    let filtered = mockPurchaseOrders;

    if (searchText) {
      filtered = filtered.filter(po =>
        po.orderNumber.toLowerCase().includes(searchText.toLowerCase()) ||
        po.vendor.name.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(po => po.status === statusFilter);
    }

    if (vendorFilter) {
      filtered = filtered.filter(po => po.vendorId === vendorFilter);
    }

    if (priorityFilter) {
      filtered = filtered.filter(po => po.priority === priorityFilter);
    }

    setFilteredData(filtered);
  }, [searchText, statusFilter, vendorFilter, priorityFilter]);

  const getStatusColor = (status: PurchaseOrderStatus): string => {
    switch (status) {
      case 'draft': return 'default';
      case 'pending_approval': return 'warning';
      case 'approved': return 'success';
      case 'sent_to_vendor': return 'processing';
      case 'acknowledged': return 'cyan';
      case 'in_production': return 'blue';
      case 'shipped': return 'purple';
      case 'partially_received': return 'orange';
      case 'received': return 'green';
      case 'invoiced': return 'geekblue';
      case 'paid': return 'lime';
      case 'cancelled': return 'red';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: Priority): string => {
    switch (priority) {
      case 'low': return 'green';
      case 'normal': return 'blue';
      case 'high': return 'orange';
      case 'urgent': return 'red';
      case 'critical': return 'purple';
      default: return 'blue';
    }
  };

  const getStatusIcon = (status: PurchaseOrderStatus) => {
    switch (status) {
      case 'approved': return <CheckCircleOutlined />;
      case 'sent_to_vendor': return <SendOutlined />;
      case 'shipped': return <TruckOutlined />;
      case 'received': return <CheckCircleOutlined />;
      case 'pending_approval': return <ClockCircleOutlined />;
      default: return <FileTextOutlined />;
    }
  };

  const getDeliveryProgress = (order: PurchaseOrder): number => {
    const now = new Date();
    const orderDate = new Date(order.orderDate);
    const deliveryDate = new Date(order.expectedDeliveryDate);
    
    const totalDays = (deliveryDate.getTime() - orderDate.getTime()) / (1000 * 60 * 60 * 24);
    const elapsedDays = (now.getTime() - orderDate.getTime()) / (1000 * 60 * 60 * 24);
    
    return Math.min(Math.max((elapsedDays / totalDays) * 100, 0), 100);
  };

  const handleAction = (record: PurchaseOrder, action: string) => {
    switch (action) {
      case 'view':
        Modal.info({
          title: `Purchase Order ${record.orderNumber}`,
          width: 800,
          content: (
            <div>
              <Row gutter={16}>
                <Col span={12}>
                  <p><strong>Vendor:</strong> {record.vendor.name}</p>
                  <p><strong>Status:</strong> <Tag color={getStatusColor(record.status)}>{record.status}</Tag></p>
                  <p><strong>Priority:</strong> <Tag color={getPriorityColor(record.priority)}>{record.priority}</Tag></p>
                  <p><strong>Total Amount:</strong> {record.currency} {record.totalAmount.toLocaleString()}</p>
                </Col>
                <Col span={12}>
                  <p><strong>Order Date:</strong> {record.orderDate.toLocaleDateString()}</p>
                  <p><strong>Expected Delivery:</strong> {record.expectedDeliveryDate.toLocaleDateString()}</p>
                  <p><strong>Created By:</strong> {record.createdBy}</p>
                </Col>
              </Row>
              <Timeline style={{ marginTop: 16 }}>
                <Timeline.Item color="blue">Purchase Order Created</Timeline.Item>
                <Timeline.Item color="green">Approved by Finance</Timeline.Item>
                <Timeline.Item color="orange">Sent to Vendor</Timeline.Item>
                <Timeline.Item color="gray">Awaiting Acknowledgment</Timeline.Item>
              </Timeline>
            </div>
          ),
        });
        break;
      case 'edit':
        onOrderUpdate(record.id, record);
        break;
      case 'delete':
        Modal.confirm({
          title: 'Delete Purchase Order',
          content: `Are you sure you want to delete purchase order ${record.orderNumber}?`,
          onOk: () => onOrderDelete(record.id),
        });
        break;
      case 'send':
        Modal.success({
          title: 'Order Sent',
          content: `Purchase order ${record.orderNumber} has been sent to ${record.vendor.name}`,
        });
        break;
    }
  };

  const actionMenu = (record: PurchaseOrder) => (
    <Menu>
      <Menu.Item key="view" icon={<EyeOutlined />} onClick={() => handleAction(record, 'view')}>
        View Details
      </Menu.Item>
      <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleAction(record, 'edit')}>
        Edit
      </Menu.Item>
      {record.status === 'approved' && (
        <Menu.Item key="send" icon={<SendOutlined />} onClick={() => handleAction(record, 'send')}>
          Send to Vendor
        </Menu.Item>
      )}
      <Menu.Divider />
      <Menu.Item key="delete" danger icon={<DeleteOutlined />} onClick={() => handleAction(record, 'delete')}>
        Delete
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: 'Order Number',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 150,
      fixed: 'left' as const,
      render: (text: string, record: PurchaseOrder) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ color: '#1890ff', cursor: 'pointer' }} onClick={() => handleAction(record, 'view')}>
            {text}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.orderDate.toLocaleDateString()}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor',
      key: 'vendor',
      width: 200,
      render: (vendor: Vendor) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} />
          <Space direction="vertical" size={0}>
            <Text strong>{vendor.name}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {vendor.contactInfo.primaryContact.name}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: Priority) => (
        <Tag color={getPriorityColor(priority)} style={{ textTransform: 'capitalize' }}>
          {priority}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (status: PurchaseOrderStatus) => (
        <Badge 
          status={getStatusColor(status) as any} 
          text={
            <Space>
              {getStatusIcon(status)}
              <span style={{ textTransform: 'capitalize' }}>
                {status.replace('_', ' ')}
              </span>
            </Space>
          } 
        />
      ),
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      render: (amount: number, record: PurchaseOrder) => (
        <Text strong style={{ color: '#1890ff' }}>
          {record.currency} {amount.toLocaleString()}
        </Text>
      ),
    },
    {
      title: 'Expected Delivery',
      dataIndex: 'expectedDeliveryDate',
      key: 'expectedDeliveryDate',
      width: 160,
      render: (date: Date, record: PurchaseOrder) => {
        const progress = getDeliveryProgress(record);
        const isOverdue = new Date(date) < new Date() && record.status !== 'received';
        
        return (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Text style={{ color: isOverdue ? '#ff4d4f' : '#000' }}>
              {date.toLocaleDateString()}
            </Text>
            <Progress 
              percent={progress} 
              size="small" 
              status={isOverdue ? 'exception' : 'normal'}
              showInfo={false}
            />
          </Space>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 120,
      render: (text: string) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 60,
      fixed: 'right' as const,
      render: (_, record: PurchaseOrder) => (
        <Dropdown overlay={actionMenu(record)} trigger={['click']} placement="bottomRight">
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  return (
    <div className="purchase-orders">
      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Orders"
              value={filteredData.length}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending Approval"
              value={filteredData.filter(o => o.status === 'pending_approval').length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="In Transit"
              value={filteredData.filter(o => o.status === 'shipped').length}
              prefix={<TruckOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Value"
              value={filteredData.reduce((sum, o) => sum + o.totalAmount, 0)}
              prefix={<DollarOutlined />}
              precision={0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Table Card */}
      <Card
        title={<Title level={4}>Purchase Orders</Title>}
        extra={
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={onOrderCreate}>
              New Purchase Order
            </Button>
            <Button icon={<ExportOutlined />}>Export</Button>
          </Space>
        }
      >
        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Input
              placeholder="Search orders..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="draft">Draft</Option>
              <Option value="pending_approval">Pending Approval</Option>
              <Option value="approved">Approved</Option>
              <Option value="sent_to_vendor">Sent to Vendor</Option>
              <Option value="acknowledged">Acknowledged</Option>
              <Option value="shipped">Shipped</Option>
              <Option value="received">Received</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Priority"
              value={priorityFilter}
              onChange={setPriorityFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="low">Low</Option>
              <Option value="normal">Normal</Option>
              <Option value="high">High</Option>
              <Option value="urgent">Urgent</Option>
              <Option value="critical">Critical</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Vendor"
              value={vendorFilter}
              onChange={setVendorFilter}
              allowClear
              style={{ width: '100%' }}
            >
              {mockVendors.map(vendor => (
                <Option key={vendor.id} value={vendor.id}>{vendor.name}</Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <RangePicker style={{ width: '100%' }} />
          </Col>
        </Row>

        {/* Bulk Actions */}
        {selectedRowKeys.length > 0 && (
          <div style={{ marginBottom: 16, padding: 8, background: '#f0f2f5', borderRadius: 4 }}>
            <Space>
              <Text>{selectedRowKeys.length} items selected</Text>
              <Button size="small">Send to Vendors</Button>
              <Button size="small">Mark as Received</Button>
              <Button size="small" danger>Cancel Orders</Button>
            </Space>
          </div>
        )}

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          scroll={{ x: 1400 }}
          pagination={{
            total: filteredData.length,
            //pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </Card>
    </div>
  );
}; 